/**
 * Compliance Reference Documents Modal
 * ----------------------------------------------------------------
 * Upload / preview / delete supporting reference documents attached
 * to a compliance SECTION or a compliance ITEM. Files are stored on
 * AWS object storage; preview streams inline via the backend.
 *
 * Upload & delete are admin-only; any AMC user can preview.
 */
import React, { useRef, useState } from 'react';
import { X, Upload, Eye, Trash2, Loader, FileText } from 'lucide-react';
import axios from 'axios';
import { complianceAPI } from '../../services/api';

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ComplianceDocsModal = ({ level, entityId, title, documents = [], isAdmin, onClose, onChanged }) => {
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const isSection = level === 'section';
  const uploadFn = isSection ? complianceAPI.uploadSectionDocument : complianceAPI.uploadItemDocument;
  const deleteFn = isSection ? complianceAPI.deleteSectionDocument : complianceAPI.deleteItemDocument;
  const previewUrl = (docId) => isSection
    ? complianceAPI.previewSectionDocumentUrl(entityId, docId)
    : complianceAPI.previewItemDocumentUrl(entityId, docId);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await uploadFn(entityId, file);
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePreview = async (doc) => {
    try {
      const res = await axios.get(previewUrl(doc.id), { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      window.open(blobUrl, '_blank');
    } catch (err) {
      alert(err.response?.data?.detail || 'Preview failed');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"?`)) return;
    setDeletingId(doc.id);
    try {
      await deleteFn(entityId, doc.id);
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="compliance-docs-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h3 className="font-semibold text-gray-900">Reference Documents</h3>
            <p className="text-xs text-gray-500 truncate max-w-sm">{title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="docs-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded px-3 py-2" data-testid="docs-modal-error">{error}</div>
          )}

          {isAdmin && (
            <div>
              <input ref={fileInputRef} type="file" onChange={handleUpload} className="hidden"
                     data-testid="docs-upload-input"
                     accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 text-sm disabled:opacity-50"
                data-testid="docs-upload-btn"
              >
                {uploading ? <Loader size={15} className="animate-spin" /> : <Upload size={15} />}
                {uploading ? 'Uploading…' : 'Upload document'}
              </button>
              <p className="text-[11px] text-gray-400 mt-1">Stored securely on AWS. PDF, images, Office docs, ZIP up to 50MB.</p>
            </div>
          )}

          {documents.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4" data-testid="docs-empty">
              No reference documents attached yet.
            </p>
          ) : (
            <ul className="divide-y" data-testid="docs-list">
              {documents.map(doc => (
                <li key={doc.id} className="py-2 flex items-center gap-3" data-testid={`doc-row-${doc.id}`}>
                  <FileText size={18} className="text-gray-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <button onClick={() => handlePreview(doc)}
                            className="text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline truncate block max-w-full text-left"
                            data-testid={`doc-name-${doc.id}`}
                            title="Click to preview">
                      {doc.filename}
                    </button>
                    <p className="text-[11px] text-gray-400">
                      {fmtSize(doc.size)}
                      {doc.uploaded_at && <> · {new Date(doc.uploaded_at).toLocaleDateString()}</>}
                    </p>
                  </div>
                  <button onClick={() => handlePreview(doc)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Preview" data-testid={`doc-preview-${doc.id}`}>
                    <Eye size={16} />
                  </button>
                  {isAdmin && (
                    <button onClick={() => handleDelete(doc)} disabled={deletingId === doc.id}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            title="Delete" data-testid={`doc-delete-${doc.id}`}>
                      {deletingId === doc.id ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceDocsModal;
